import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { v, Checkbox, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, COTENANT_SUFFIX } from '../../../parties/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). The editor field-
    // groups do NOT map 1:1 to numbered document sections, so `field.section`
    // is a plain editor label (party/property fields are realised by the
    // dedicated panels); numbering + anchors come purely from these refs +
    // the Body (the "document-only" migration, like andring_hyresvillkor).
    sections: [
        { ref: 'parter', title: 'Parter' },
        { ref: 'uppsagning', title: 'Uppsägning till hyresgästen' },
        { ref: 'meddelande', title: 'Meddelande' },
        { ref: 'underskrift', title: 'Underskrift (hyresvärd)' },
        { ref: 'erkannande', title: 'Erkännande (hyresgäst)' },
    ],
    templateKey: 'uppsagning_villkorsandring_lokal',
    version: 'v1',
    category: 'Lokalhyra',
    title: 'Uppsägning av lokalhyresavtal för villkorsändring',
    shortTitle: 'Uppsägning villkorsändring (lokal)',
    titleSuffix: { key: 'contract_number', prefix: 'avtal' },
    formNumber: 'Formulär R-19',
    preamble: 'Hyresvärdens uppsägning av lokalhyresavtalet för villkorsändring enligt 12 kap. jordabalken.',
    description: 'Hyresvärdens uppsägning av ett lokalhyresavtal för villkorsändring (12 kap. 58 § JB). Använd när hyresvärden vill ändra villkoren för en lokalhyresgäst och säger upp avtalet för omförhandling.\n\n' +
        'Vem undertecknar: hyresvärden (uppsägningen) och hyresgästen (erkännande om mottagande). Skickas via Scrive.\n\n' +
        'Hyresgästen har indirekt besittningsskydd: om förlängning inte kommer till stånd måste hyresgästen hänskjuta tvisten till hyresnämnden för medling inom två månader från uppsägningen, annars förloras rätten till ersättning enligt 12 kap. 58 b § JB.\n\n' +
        'Kopplad mall — skapas under det lokalhyresavtal som sägs upp. Parter, lokal och avtalsnummer förförifylls från lokalavtalet (även om det är ett importerat avtal).',
    status: 'active',
    signingMode: 'scrive',
    connectedTo: ['lokalhyra'],
    signingPlacementAnchors: 'per-party-label',
    // Same lease, same parties + lokal (ADR-0055). Roles line up 1:1 with the
    // parent lokalhyresavtalet (lessor0 = hyresvärd, lessee0/1 = hyresgäst),
    // so by-role-ordinal is correct. `contract_number` carries the original
    // avtalsnummer the uppsägning refers to. Works on imported parents too.
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [{ key: 'contract_number', fromParentKey: 'contract_number' }],
    },
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om lokalavtalet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065) — editor fields only; body renders parties
    // under role labels (Hyresvärd/Hyresgäst), so it is not PartyBlock-driven.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärd',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'Hyresgäst',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_org_number' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'Hyresgäst',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_org_number' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Standardfält om hyresavtalet
    {
        key: 'contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        section: 'Hyreskontrakt',
        requiredAtFinal: true,
        help: 'Numret på det lokalhyresavtal som sägs upp.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    // Parter — generated from metadata.partyProjection (ADR-0065). The
    // document body renders these under role labels; the projection drives
    // the editor fields + autofill.
    ...expandPartyProjection(metadata),
    // Lokalen
    {
        key: 'property_address',
        label: 'Fastighetsadress',
        type: 'textarea',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    // Uppsägning
    {
        key: 'termination_date',
        label: 'Upphörande den',
        type: 'date',
        section: 'Uppsägning',
        requiredAtFinal: true,
        help: 'Datum då hyresavtalet upphör att gälla.',
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Meddelande
    {
        key: 'extension_terms_mode',
        label: 'För förlängning begärs',
        type: 'select',
        section: 'Meddelande',
        requiredAtFinal: true,
        default: 'alt1',
        options: [
            { value: 'alt1', label: 'Alt 1 — villkor enligt bifogat förslag till hyresavtal', code: 'forslag_bifogas', summary: 'Extension terms are set out in an attached draft lease agreement.' },
            { value: 'alt2', label: 'Alt 2 — följande ändrade hyresvillkor (anges nedan)', code: 'andrade_villkor_nedan', summary: 'Changed lease terms are written directly into the notice below.' },
        ],
        help: 'Alt 1: ett förslag till nytt hyresavtal bifogas (lägg till det under Bilagor). ' +
            'Alt 2: de ändrade villkoren skrivs in direkt i meddelandet.',
    },
    {
        key: 'alt2_changed_terms',
        label: 'Ändrade hyresvillkor',
        type: 'textarea',
        section: 'Meddelande',
        visibleWhen: { extension_terms_mode: 'alt2' },
        requiredWhen: { extension_terms_mode: 'alt2' },
        help: 'Beskriv de hyresvillkor som begärs för förlängning.',
    },
    {
        key: 'include_unchanged_clause',
        label: 'Lägg till "I övrigt oförändrade villkor"',
        type: 'boolean',
        section: 'Meddelande',
        default: true,
        help: 'Anger att hyresavtalet i övrigt ska löpa på oförändrade villkor. Valfri vid Alt 1, standard vid Alt 2.',
    },
    // Display-only editor note (type: 'note') — never read by the Body, never
    // rendered into the document. Påminnelse till hyresvärden om delgivning.
    {
        key: 'delgivning_note',
        label: 'Observera – delgivning',
        type: 'note',
        section: 'Meddelande',
        help: 'Uppsägningen måste vara delgiven hyresgästen senast sista dagen för uppsägning. Säkerställ att delgivning kan styrkas.',
    },
];
export const sampleValues = {
    contract_number: 'L-3087',
    property_designation: 'Verkstaden 4',
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    tenant_name: 'Café Söder HB',
    tenant_org_number: '969712-5678',
    cotenant_name: '',
    cotenant_org_number: '',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    property_municipality: 'Stockholm',
    termination_date: '2026-12-31',
    extension_terms_mode: 'alt2',
    alt2_changed_terms: 'Hyran höjs till 480 000 kr per år. Hyrestiden förlängs med tre år med oförändrad indexklausul.',
    include_unchanged_clause: true,
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    const isAlt1 = values.extension_terms_mode !== 'alt2';
    const isAlt2 = values.extension_terms_mode === 'alt2';
    const showAlt2Terms = cond(mode, isAlt2);
    const showUnchanged = cond(mode, values.include_unchanged_clause !== false);
    const showCotenant = cond(mode, !!values.cotenant_name);
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-3 text-[12.5px] font-semibold", children: "Upps\u00E4gning av lokalhyresavtal f\u00F6r villkors\u00E4ndring" }), _jsx(Section, { sectionRef: "parter", title: "Parter", children: _jsxs("div", { className: "border border-stone-300", children: [_jsx(FieldRow, { label: "Hyreskontrakt nr", children: v(values.contract_number) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Hyresg\u00E4st", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.tenant_org_number) }), showCotenant && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Hyresg\u00E4st (medhyresg\u00E4st)", children: v(values.cotenant_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.cotenant_org_number) })] }))] }) }), _jsx(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning till hyresg\u00E4sten", children: _jsxs("div", { className: "border border-stone-300", children: [_jsx(FieldRow, { label: "Fastighetsadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Upph\u00F6rande den", children: v(values.termination_date) })] }) }), _jsxs(Section, { sectionRef: "meddelande", title: "Meddelande", children: [_jsxs("p", { className: "font-semibold", children: ["Hyresv\u00E4rden s\u00E4ger upp hyresavtalet f\u00F6r villkors\u00E4ndring till den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.termination_date) }), "."] }), _jsxs("p", { className: "mt-3", children: [_jsx(Checkbox, { checked: cond(mode, isAlt1) }), _jsx("span", { className: "font-semibold", children: "Alt 1:" }), " F\u00F6r f\u00F6rl\u00E4ngning beg\u00E4rs villkor enligt bifogat f\u00F6rslag till hyresavtal."] }), _jsxs("p", { className: "mt-2", children: [_jsx(Checkbox, { checked: cond(mode, isAlt2) }), _jsx("span", { className: "font-semibold", children: "Alt 2:" }), " F\u00F6r f\u00F6rl\u00E4ngning beg\u00E4rs f\u00F6ljande \u00E4ndrade hyresvillkor:"] }), showAlt2Terms && (_jsx("div", { className: "my-2 min-h-[3rem] whitespace-pre-line border border-stone-400 px-3 py-2 font-serif text-[10.5px] leading-relaxed", children: v(values.alt2_changed_terms) })), showUnchanged && (_jsx("p", { className: "mt-3", children: "I \u00F6vrigt ska hyresavtalet l\u00F6pa p\u00E5 of\u00F6r\u00E4ndrade villkor." })), _jsx("p", { className: "mt-3", children: "Om f\u00F6rl\u00E4ngning inte kan komma till st\u00E5nd och ni inte g\u00E5r med p\u00E5 att flytta utan ers\u00E4ttning enligt 12 kap. 58 b \u00A7 JB, har ni att inom tv\u00E5 m\u00E5nader fr\u00E5n denna upps\u00E4gning h\u00E4nskjuta tvisten till hyresn\u00E4mnden f\u00F6r medling." })] }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrift", title: "Underskrift (hyresv\u00E4rd)", children: _jsx(SigningBlock, { parties: [{ label: 'Hyresvärd', role: 'lessor', ordinal: 0 }], signatories: signatories }) }) }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "erkannande", title: "Erk\u00E4nnande (hyresg\u00E4st)", children: [_jsx("p", { className: "mb-4", children: "Jag har denna dag tagit emot en med denna likalydande handling." }), _jsx(SigningBlock, { parties: showCotenant
                                ? [
                                    { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                                    { label: 'Medhyresgäst', role: 'lessee', ordinal: 1 },
                                ]
                                : [{ label: 'Hyresgäst', role: 'lessee', ordinal: 0 }], signatories: signatories })] }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map